// One issue's workspace, read beside the plan snapshot.
//
// Same discipline as `usePrivateThread`: a load replaces the view, never merges into it, and
// the last load started wins. It is read again whenever the plan snapshot changes, which is
// what every signal and poll tick produces, so a change Mor makes in a reply shows on the
// page with no second stream.

import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiFailure, type ApiClient } from '../api/client.js';
import type { IssueLoad } from './issue.js';

export type IssueState = IssueLoad & { refetch: () => void };

export function useIssue(
  client: ApiClient,
  planId: string,
  issueId: string | null,
  refreshOn: unknown,
): IssueState {
  const [load, setLoad] = useState<IssueLoad>({ status: 'loading', view: null });
  const generation = useRef(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const read = useCallback(() => {
    const mine = (generation.current += 1);
    if (issueId === null) {
      return;
    }
    client.readIssue(planId, issueId).then(
      (view) => {
        if (alive.current && mine === generation.current) {
          setLoad({ status: 'ready', view });
        }
      },
      (error: unknown) => {
        if (!alive.current || mine !== generation.current) {
          return;
        }
        if (error instanceof ApiFailure && error.status === 404) {
          setLoad({ status: 'missing', view: null });
        } else {
          // A view that was read once stays on screen.
          setLoad((now) => ({ status: 'faltered', view: now.view }));
        }
      },
    );
  }, [client, planId, issueId]);

  // Another issue is a fresh start.
  useEffect(() => {
    setLoad({ status: 'loading', view: null });
  }, [planId, issueId]);

  useEffect(() => {
    read();
  }, [read, refreshOn]);

  return { ...load, refetch: read } as IssueState;
}
